import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import type { FileExplanation } from "@/data/file-explanations"

interface FileExplanationPanelProps {
  file: FileExplanation | null
  onClose: () => void
}

export function FileExplanationPanel({ file, onClose }: FileExplanationPanelProps) {
  if (!file) return null

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-background border-l shadow-lg z-50 flex flex-col">
      <div className="flex items-center justify-between border-b p-4">
        <h3 className="font-semibold">File Details</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <div>
            <p className="text-xs text-muted-foreground mb-2">File Path</p>
            <code className="text-sm bg-muted px-2 py-1 rounded block break-all">
              {file.path}
            </code>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Description</h4>
            <p className="text-sm text-muted-foreground">{file.description}</p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Purpose</h4>
            <p className="text-sm text-muted-foreground">{file.purpose}</p>
          </div>

          {file.keyFeatures && file.keyFeatures.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Key Features</h4>
              <ul className="space-y-2">
                {file.keyFeatures.map((feature, index) => (
                  <li key={index} className="flex gap-2 text-sm">
                    <span className="text-primary shrink-0">•</span>
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {file.relatedFiles && file.relatedFiles.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Related Files</h4>
              <div className="flex flex-wrap gap-2">
                {file.relatedFiles.map((relatedFile, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {relatedFile}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

